import { Fragment, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest, queryClient } from "../lib/api";
import { formatDateTime, formatNumber } from "../lib/utils";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

interface TicketRow {
  id: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  source: string | null;
  client_id: string | null;
  client_name: string | null;
  assigned_to: string | null;
  assigned_admin_email: string | null;
  last_reply_at: string | null;
  created_at: string;
  updated_at: string;
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  telegram_username: string | null;
  message_count: number;
  last_message_preview: string | null;
  last_message_at: string | null;
  needs_reply: boolean | string | null;
  tg_from_user_id: string | null;
  tg_from_name: string | null;
  tg_from_username: string | null;
  tg_chat_title: string | null;
  tg_chat_id: string | null;
}

interface AdminListItem {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
}

interface TicketsResponse {
  tickets: TicketRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface TicketStats {
  openCount: number;
  inProgressCount: number;
  waitingUserCount: number;
  resolvedCount: number;
  closedCount: number;
  totalCount: number;
  newToday: number;
  needsReplyCount: number;
  waitingClientCount: number;
}

interface AutoDiagnostics {
  schemaReady: boolean;
  autoEnabled: boolean;
  unmappedAutoEnabled: boolean;
  fallbackUserConfigured: boolean;
  clientChannelsCount: number;
  clientsWithPrimaryUser: number;
  ticketsBySource: Record<string, number>;
  telegramMessagesLast7d: number;
  maxMessagesLast7d: number;
  pendingTelegramBackfill: number;
  pendingMaxBackfill: number;
  unmappedTelegramChatsLast7d: Array<{
    chatId: number;
    chatTitle: string | null;
    messageCount: number;
  }>;
  sampleClientChannels: Array<{
    clientName: string;
    channelType: string;
    chatId: number | null;
    title: string | null;
  }>;
}

interface BackfillResult {
  dryRun: boolean;
  scanned: number;
  created: number;
  skipped: number;
  errors: number;
  previews: Array<{ source: string; sourceRef: string; subject: string; clientName: string }>;
}

interface MergeTelegramResult {
  dryRun: boolean;
  ticketsScanned: number;
  metadataEnriched: number;
  groupsWithDuplicates: number;
  groupsMerged: number;
  ticketsRemoved: number;
  messagesMoved: number;
  previews: Array<{
    threadKey: string;
    chatTitle: string | null;
    fromName: string | null;
    canonicalId: string;
    mergedTicketIds: string[];
    totalMessages: number;
  }>;
}

interface UnmappedChat {
  chatId: number;
  chatTitle: string | null;
  chatType: string | null;
  messageCount: number;
  lastSentAt: string;
  lastMessagePreview: string | null;
}

interface ClientPickerRow {
  id: string;
  slug: string;
  name: string;
  primaryUserId: string | null;
  primaryEmail: string | null;
}

function getStatusBadge(status: string) {
  const map: Record<string, { label: string; className: string }> = {
    open: { label: "Открыт", className: "bg-blue-100 text-blue-700" },
    in_progress: { label: "В работе", className: "bg-yellow-100 text-yellow-700" },
    waiting_user: { label: "Ожидает ответа", className: "bg-orange-100 text-orange-700" },
    resolved: { label: "Решён", className: "bg-green-100 text-green-700" },
    closed: { label: "Закрыт", className: "bg-gray-100 text-gray-600" },
  };
  const item = map[status] || { label: status, className: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${item.className}`}>
      {item.label}
    </span>
  );
}

function getPriorityBadge(priority: string) {
  const map: Record<string, { label: string; className: string }> = {
    low: { label: "Низкий", className: "bg-slate-100 text-slate-600" },
    normal: { label: "Обычный", className: "bg-blue-50 text-blue-600" },
    high: { label: "Высокий", className: "bg-orange-100 text-orange-700" },
    urgent: { label: "Срочный", className: "bg-red-100 text-red-700" },
  };
  const item = map[priority] || { label: priority, className: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${item.className}`}>
      {item.label}
    </span>
  );
}

const SOURCE_TABS = [
  { id: "", label: "Все" },
  { id: "telegram", label: "Telegram" },
  { id: "max", label: "Max" },
  { id: "lk", label: "ЛК" },
  { id: "manual", label: "Вручную" },
  { id: "escalation", label: "Эскалация" },
] as const;

const SOURCE_GROUP_ORDER = ["telegram", "max", "lk", "manual", "escalation"] as const;

const WORK_QUEUE_TABS = [
  { id: "needs_reply", label: "Нужен мой ответ" },
  { id: "waiting_client", label: "Жду клиента" },
  { id: "active", label: "Все активные" },
  { id: "", label: "Все тикеты" },
] as const;

function formatWaitingSince(iso: string | null): string {
  if (!iso) return "без сообщений";
  const hours = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (hours < 1) return "меньше часа";
  if (hours < 24) return `${Math.max(1, Math.floor(hours))} ч`;
  return `${Math.floor(hours / 24)} дн.`;
}

function ticketNeedsReply(t: TicketRow): boolean {
  return t.needs_reply === true || t.needs_reply === "true";
}

function getSourceLabel(source: string | null) {
  const map: Record<string, string> = {
    lk: "ЛК",
    telegram: "Telegram",
    max: "Max",
    manual: "Вручную",
    escalation: "Эскалация",
  };
  return map[source || ""] || source || "—";
}

function getCategoryLabel(category: string) {
  const map: Record<string, string> = {
    general: "Общий",
    billing: "Биллинг",
    technical: "Техническая",
    feature_request: "Предложение",
  };
  return map[category] || category;
}

export default function TicketsPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [source, setSource] = useState("");
  const [workQueue, setWorkQueue] = useState("needs_reply");
  const [search, setSearch] = useState("");
  const limit = 20;

  const searchQuery = search.length >= 2 ? search : "";

  const { data: adminsList } = useQuery<AdminListItem[]>({
    queryKey: ["/api/admin/admins-list"],
  });

  const listSort =
    workQueue === "needs_reply" ? "&sort=last_message_at&order=asc" : "&sort=created_at&order=desc";

  const { data, isLoading } = useQuery<TicketsResponse>({
    queryKey: [
      `/api/admin/tickets?page=${page}&limit=${limit}${status ? `&status=${status}` : ""}${category ? `&category=${category}` : ""}${priority ? `&priority=${priority}` : ""}${assignedTo ? `&assignedTo=${assignedTo}` : ""}${source ? `&source=${source}` : ""}${workQueue ? `&queue=${workQueue}` : ""}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ""}${listSort}`,
    ],
  });

  const { data: statsData } = useQuery<TicketStats>({
    queryKey: ["/api/admin/tickets/stats"],
  });

  const { data: autoDiag, refetch: refetchDiag } = useQuery<AutoDiagnostics>({
    queryKey: ["/api/admin/tickets/auto-diagnostics"],
  });

  const { data: unmappedChats, refetch: refetchUnmapped } = useQuery<{
    chats: UnmappedChat[];
  }>({
    queryKey: ["/api/admin/telegram-chats/unmapped?days=90"],
  });

  const { data: clientsPicker } = useQuery<ClientPickerRow[]>({
    queryKey: ["/api/admin/clients/registry-picker"],
  });

  const [linkClientByChat, setLinkClientByChat] = useState<Record<number, string>>({});
  const [backfillPreview, setBackfillPreview] = useState<BackfillResult | null>(null);
  const [mergePreview, setMergePreview] = useState<MergeTelegramResult | null>(null);

  const previewBackfillMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/tickets/backfill", {
        days: 7,
        limit: 30,
        dryRun: true,
      });
      return (await res.json()) as BackfillResult;
    },
    onSuccess: (result) => setBackfillPreview(result),
  });

  const linkChatMutation = useMutation({
    mutationFn: async ({
      chatId,
      clientId,
      title,
    }: {
      chatId: number;
      clientId: string;
      title: string | null;
    }) => {
      const res = await apiRequest("POST", "/api/admin/client-channels/link-telegram", {
        chatId,
        clientId,
        title,
        isPrimary: true,
      });
      return res.json();
    },
    onSuccess: () => {
      refetchUnmapped();
      refetchDiag();
    },
  });

  const previewMergeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/tickets/merge-telegram", {
        limit: 500,
        dryRun: true,
      });
      return (await res.json()) as MergeTelegramResult;
    },
    onSuccess: (result) => setMergePreview(result),
  });

  const applyMergeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/tickets/merge-telegram/apply", {
        limit: 500,
      });
      return (await res.json()) as MergeTelegramResult;
    },
    onSuccess: () => {
      setMergePreview(null);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tickets/stats"] });
      queryClient.invalidateQueries({
        predicate: (q) =>
          typeof q.queryKey[0] === "string" && String(q.queryKey[0]).startsWith("/api/admin/tickets?"),
      });
      refetchDiag();
    },
  });

  const applyBackfillMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/tickets/backfill/apply", {
        days: 7,
        limit: 30,
      });
      return (await res.json()) as BackfillResult;
    },
    onSuccess: () => {
      setBackfillPreview(null);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/tickets/stats"] });
      queryClient.invalidateQueries({
        predicate: (q) =>
          typeof q.queryKey[0] === "string" && String(q.queryKey[0]).startsWith("/api/admin/tickets?"),
      });
      refetchDiag();
    },
  });

  const sourceCounts = useMemo(() => {
    const by = autoDiag?.ticketsBySource ?? {};
    return {
      "": statsData?.totalCount ?? 0,
      telegram: by.telegram ?? 0,
      max: by.max ?? 0,
      lk: by.lk ?? 0,
      manual: by.manual ?? 0,
      escalation: by.escalation ?? 0,
    } as Record<string, number>;
  }, [autoDiag, statsData]);

  const groupedBySender = useMemo(() => {
    if (workQueue || source !== "telegram" || !data?.tickets.length) return null;
    const groups = new Map<string, { label: string; tickets: TicketRow[] }>();
    for (const t of data.tickets) {
      const key =
        t.tg_from_user_id && t.tg_chat_id
          ? `${t.tg_chat_id}:${t.tg_from_user_id}`
          : `solo:${t.id}`;
      const sender =
        t.tg_from_name ||
        (t.tg_from_username ? `@${t.tg_from_username}` : null) ||
        (t.tg_from_user_id ? `TG #${t.tg_from_user_id}` : "Неизвестный");
      const chat = t.tg_chat_title || (t.tg_chat_id ? `чат ${t.tg_chat_id}` : "");
      const label = chat ? `${sender} · ${chat}` : sender;
      if (!groups.has(key)) groups.set(key, { label, tickets: [] });
      groups.get(key)!.tickets.push(t);
    }
    return [...groups.values()];
  }, [data?.tickets, source, workQueue]);

  const groupedTickets = useMemo(() => {
    if (workQueue || source || !data?.tickets.length) return null;
    const groups = new Map<string, TicketRow[]>();
    for (const t of data.tickets) {
      const key = t.source || "lk";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(t);
    }
    const extra = [...groups.keys()].filter(
      (k) => !SOURCE_GROUP_ORDER.includes(k as (typeof SOURCE_GROUP_ORDER)[number]),
    );
    const order = [...SOURCE_GROUP_ORDER, ...extra];
    return order
      .filter((k) => groups.has(k))
      .map((k) => ({ source: k, tickets: groups.get(k)! }));
  }, [data?.tickets, source, workQueue]);

  const showTelegramSetup = source === "telegram";
  const showMaxSetup = source === "max";
  const ticketsWithoutClientLink =
    autoDiag?.unmappedAutoEnabled && autoDiag?.fallbackUserConfigured;

  const renderTicketRow = (t: TicketRow) => {
    const tgSender =
      t.tg_from_name ||
      (t.tg_from_username ? `@${t.tg_from_username}` : null) ||
      (t.tg_from_user_id ? `TG #${t.tg_from_user_id}` : null);

    const needsReply = ticketNeedsReply(t);
    const waitingHours =
      t.last_message_at != null
        ? (Date.now() - new Date(t.last_message_at).getTime()) / 3_600_000
        : 0;

    return (
    <tr
      key={t.id}
      className={`hover:bg-muted/30 ${needsReply ? "bg-amber-50/50 dark:bg-amber-950/20" : ""}`}
    >
      <td className="px-4 py-2 max-w-[280px]">
        <Link href={`/tickets/${t.id}`}>
          <span className="text-sm text-primary hover:underline cursor-pointer font-medium line-clamp-1">
            {t.subject}
          </span>
        </Link>
        {t.last_message_preview && (
          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{t.last_message_preview}</p>
        )}
        {needsReply && t.last_message_at && (
          <span
            className={`inline-block mt-1 text-xs font-medium px-1.5 py-0.5 rounded ${
              waitingHours >= 48
                ? "bg-red-100 text-red-800"
                : waitingHours >= 24
                  ? "bg-orange-100 text-orange-800"
                  : "bg-amber-100 text-amber-800"
            }`}
          >
            ждёт ответа {formatWaitingSince(t.last_message_at)}
          </span>
        )}
      </td>
      <td className="px-4 py-2">
        {tgSender ? (
          <div className="text-sm">
            <div className="font-medium">{tgSender}</div>
            {t.client_name ? (
              <div className="text-xs text-muted-foreground truncate max-w-[160px]">
                {t.client_name}
              </div>
            ) : t.tg_chat_title ? (
              <div className="text-xs text-muted-foreground truncate max-w-[160px]">
                {t.tg_chat_title}
              </div>
            ) : null}
          </div>
        ) : (
          <Link href={`/users/${t.user_id}`}>
            <span className="text-sm text-primary hover:underline cursor-pointer">{t.email}</span>
          </Link>
        )}
      </td>
      <td className="px-4 py-2 text-sm text-muted-foreground">{getCategoryLabel(t.category)}</td>
      <td className="px-4 py-2">{getPriorityBadge(t.priority)}</td>
      <td className="px-4 py-2">{getStatusBadge(t.status)}</td>
      {!source && (
        <td className="px-4 py-2 text-sm text-muted-foreground">
          {getSourceLabel(t.source)}
          {t.client_name ? (
            <div className="text-xs truncate max-w-[120px]">{t.client_name}</div>
          ) : null}
        </td>
      )}
      <td className="px-4 py-2 text-sm text-muted-foreground truncate max-w-[140px]">
        {t.assigned_admin_email || "—"}
      </td>
      <td className="px-4 py-2 text-sm text-center">{t.message_count}</td>
      <td className="px-4 py-2 text-sm text-muted-foreground">{formatDateTime(t.created_at)}</td>
      <td className="px-4 py-2 text-sm text-muted-foreground">
        {t.last_reply_at ? formatDateTime(t.last_reply_at) : "—"}
      </td>
    </tr>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Тикеты</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Рабочая очередь: сначала то, где последнее слово за клиентом
          </p>
        </div>
        {data && (
          <span className="text-sm text-muted-foreground">
            Найдено: {formatNumber(data.total)}
          </span>
        )}
      </div>

      <div
        className="flex flex-wrap gap-1 p-1 rounded-lg border border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/30"
        role="tablist"
        aria-label="Рабочая очередь"
      >
        {WORK_QUEUE_TABS.map((tab) => {
          const count =
            tab.id === "needs_reply"
              ? statsData?.needsReplyCount
              : tab.id === "waiting_client"
                ? statsData?.waitingClientCount
                : tab.id === "active"
                  ? (statsData?.openCount ?? 0) +
                    (statsData?.inProgressCount ?? 0) +
                    (statsData?.waitingUserCount ?? 0)
                  : statsData?.totalCount;
          const active = workQueue === tab.id;
          return (
            <button
              key={tab.id || "all"}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                setWorkQueue(tab.id);
                setStatus("");
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                active
                  ? "bg-background text-foreground shadow-sm ring-1 ring-amber-300"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
              {count != null ? (
                <span className="ml-1.5 text-xs opacity-80">{formatNumber(count)}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div
        className="flex flex-wrap gap-1 p-1 rounded-lg border border-border bg-muted/30"
        role="tablist"
        aria-label="Источник тикетов"
      >
        {SOURCE_TABS.map((tab) => {
          const count = sourceCounts[tab.id];
          const active = source === tab.id;
          return (
            <button
              key={tab.id || "all"}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                setSource(tab.id);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                active
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
              {count != null ? (
                <span className="ml-1.5 text-xs opacity-70">{formatNumber(count)}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {source === "" && autoDiag && (
        <p className="text-sm text-muted-foreground -mt-2">
          Список ниже сгруппирован по источникам. Привязка Telegram-групп и догон архива — во вкладке{" "}
          <button
            type="button"
            className="text-primary underline-offset-2 hover:underline"
            onClick={() => {
              setSource("telegram");
              setPage(1);
            }}
          >
            Telegram
          </button>
          .
        </p>
      )}

      {/* Настройка Telegram */}
      {autoDiag && showTelegramSetup && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1 min-w-0">
              <h2 className="text-base font-semibold">Telegram → тикеты</h2>
              <p className="text-sm text-muted-foreground max-w-2xl">
                Сообщения из групп ассистента собираются в тикеты по отправителю Telegram (один человек
                — один открытый тикет). Привязка к клиенту в реестре опциональна — для ЛК владельца;
                без неё тикеты видны здесь, в админке.
              </p>
            </div>
            {autoDiag.schemaReady && (
              <span
                className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium ${
                  autoDiag.autoEnabled
                    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
                    : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200"
                }`}
              >
                {autoDiag.autoEnabled ? "Автосоздание включено" : "Автосоздание выключено"}
              </span>
            )}
          </div>

          {autoDiag.schemaReady && autoDiag.autoEnabled && (
            <p className="text-sm text-emerald-700 dark:text-emerald-400">
              Новые сообщения в группах создают тикеты автоматически
              {autoDiag.unmappedAutoEnabled && autoDiag.fallbackUserConfigured
                ? " (включая непривязанные чаты)."
                : " (только привязанные к клиенту)."}
            </p>
          )}
          {autoDiag.schemaReady &&
            autoDiag.autoEnabled &&
            autoDiag.unmappedAutoEnabled &&
            !autoDiag.fallbackUserConfigured && (
              <p className="text-sm text-amber-700">
                Для непривязанных групп задайте{" "}
                <code className="text-xs">SUPPORT_TICKET_AUTO_FALLBACK_USER_ID</code> на сервере (или
                клиента internal с primary_user_id).
              </p>
            )}
          {!autoDiag.schemaReady && (
            <p className="text-sm text-red-600">
              Схема БД не готова (миграция 0027) — автотикеты и догон за 7 дней недоступны.
            </p>
          )}
          {autoDiag.schemaReady && !autoDiag.autoEnabled && (
            <p className="text-sm text-amber-700">
              На сервере выключено автосоздание тикетов. Новые сообщения в группах не откроют тикет
              сами — можно догнать архив кнопкой «Создать тикеты за 7 дней» или включить переменную
              на обоих приложениях Dokku: <code className="text-xs">gptmanager</code> и{" "}
              <code className="text-xs">gptmanager-admin</code>.
            </p>
          )}

          <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
            <li>Новые сообщения группируются по отправителю в один открытый тикет.</li>
            <li>
              Опционально: привязать группу к клиенту (таблица ниже), чтобы тикет был в ЛК владельца.
            </li>
            <li>При необходимости — догнать архив за 7 дней или объединить старые дубли.</li>
          </ol>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
            <span className="text-sm font-medium">Объединить старые дубли (1 сообщение = 1 тикет)</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => previewMergeMutation.mutate()}
                disabled={previewMergeMutation.isPending || !autoDiag.schemaReady}
                className="px-3 py-1.5 text-sm rounded-md border border-input hover:bg-muted disabled:opacity-50"
              >
                {previewMergeMutation.isPending ? "Считаю…" : "Превью объединения"}
              </button>
              <button
                type="button"
                onClick={() => applyMergeMutation.mutate()}
                disabled={applyMergeMutation.isPending || !autoDiag.schemaReady}
                className="px-3 py-1.5 text-sm rounded-md border border-amber-600 text-amber-800 hover:bg-amber-50 disabled:opacity-50 dark:hover:bg-amber-950"
              >
                {applyMergeMutation.isPending ? "Объединяю…" : "Объединить дубли"}
              </button>
            </div>
          </div>
          {mergePreview && (
            <div className="text-xs border border-border rounded-md p-2 space-y-1 bg-muted/20">
              <p>
                Групп с дублями: {mergePreview.groupsWithDuplicates}, будет объединено:{" "}
                {mergePreview.groupsMerged}, тикетов удалится: {mergePreview.ticketsRemoved},
                сообщений перенесётся: {mergePreview.messagesMoved}
                {mergePreview.metadataEnriched > 0
                  ? `, metadata дополнено: ${mergePreview.metadataEnriched}`
                  : ""}
              </p>
              {mergePreview.previews.slice(0, 5).map((p) => (
                <p key={p.threadKey} className="text-muted-foreground truncate">
                  {p.fromName ?? p.threadKey} · {p.chatTitle ?? "чат"} — {p.totalMessages} сообщ., −
                  {p.mergedTicketIds.length} тикетов
                </p>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
            <span className="text-sm font-medium">Догон за последние 7 дней</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => previewBackfillMutation.mutate()}
                disabled={previewBackfillMutation.isPending || !autoDiag.schemaReady}
                className="px-3 py-1.5 text-sm rounded-md border border-input hover:bg-muted disabled:opacity-50"
                title="Показать список тикетов без записи в БД"
              >
                {previewBackfillMutation.isPending ? "Считаю…" : "Превью"}
              </button>
              <button
                type="button"
                onClick={() => applyBackfillMutation.mutate()}
                disabled={
                  applyBackfillMutation.isPending ||
                  !autoDiag.schemaReady ||
                  (autoDiag.pendingTelegramBackfill === 0 && autoDiag.pendingMaxBackfill === 0)
                }
                className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                title={
                  ticketsWithoutClientLink
                    ? "Создать тикеты из архива по всем группам ассистента"
                    : "Создать тикеты по привязанным чатам"
                }
              >
                {applyBackfillMutation.isPending ? "Создаю…" : "Создать тикеты за 7 дней"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Привязанных групп</span>
              <div className="font-medium">{autoDiag.clientChannelsCount}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Клиентов с ЛК</span>
              <div className="font-medium">{autoDiag.clientsWithPrimaryUser}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Можно догнать за 7 дней</span>
              <div className="font-medium">{autoDiag.pendingTelegramBackfill}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Сообщений за 7 дней</span>
              <div className="font-medium">{autoDiag.telegramMessagesLast7d}</div>
            </div>
          </div>
          {Object.keys(autoDiag.ticketsBySource).length > 0 && (
            <p className="text-xs text-muted-foreground">
              Уже в системе:{" "}
              {Object.entries(autoDiag.ticketsBySource)
                .map(([k, v]) => `${k}: ${v}`)
                .join(", ")}
            </p>
          )}
          {autoDiag.clientChannelsCount === 0 && !ticketsWithoutClientLink && (
            <p className="text-sm text-amber-700">
              Нет привязанных групп и не настроен fallback-пользователь — задайте{" "}
              <code className="text-xs">SUPPORT_TICKET_AUTO_FALLBACK_USER_ID</code> на сервере или
              привяжите группу к клиенту ниже.
            </p>
          )}

          {ticketsWithoutClientLink && (
            <p className="text-sm text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900 rounded-md px-3 py-2">
              Эти {unmappedChats?.chats.length ?? 0} групп уже обрабатываются без привязки: тикеты
              появляются в списке выше (вкладка Telegram), сгруппированы по отправителю. Таблица ниже
              нужна только если хотите, чтобы клиент видел тикеты в своём ЛК.
            </p>
          )}

          <details className="pt-1 group" open={!ticketsWithoutClientLink}>
            <summary className="text-sm font-medium cursor-pointer list-none flex items-center gap-2">
              <span className="group-open:rotate-90 transition-transform">▸</span>
              Привязка групп к клиенту в реестре
              {unmappedChats ? ` (${unmappedChats.chats.length} без привязки)` : ""}
              {ticketsWithoutClientLink ? (
                <span className="text-xs font-normal text-muted-foreground">— опционально</span>
              ) : null}
            </summary>
            <p className="text-sm text-muted-foreground mt-2">
              Связь «группа → карточка клиента» для личного кабинета. На работу тикетов в админке не
              влияет, если включён fallback-пользователь.
            </p>
            {unmappedChats && unmappedChats.chats.length > 0 ? (
              <div className="overflow-x-auto border border-border rounded-md mt-2">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-2 py-1 text-left">Название группы</th>
                      <th className="px-2 py-1 text-left">chat_id</th>
                      <th className="px-2 py-1 text-left">Клиент (ЛК)</th>
                      <th className="px-2 py-1" />
                    </tr>
                  </thead>
                  <tbody>
                    {unmappedChats.chats.map((c) => (
                      <tr key={c.chatId} className="border-t border-border">
                        <td className="px-2 py-1.5">
                          <div className="font-medium">{c.chatTitle || "—"}</div>
                          {c.lastMessagePreview && (
                            <div className="text-muted-foreground truncate max-w-[240px]">
                              {c.lastMessagePreview}
                            </div>
                          )}
                          <div className="text-muted-foreground">{c.messageCount} сообщ.</div>
                        </td>
                        <td className="px-2 py-1.5 font-mono whitespace-nowrap">{c.chatId}</td>
                        <td className="px-2 py-1.5">
                          <select
                            className="w-full max-w-[200px] px-2 py-1 rounded border border-input bg-background"
                            value={linkClientByChat[c.chatId] ?? ""}
                            onChange={(e) =>
                              setLinkClientByChat((prev) => ({
                                ...prev,
                                [c.chatId]: e.target.value,
                              }))
                            }
                          >
                            <option value="">Выберите клиента…</option>
                            {clientsPicker?.map((cl) => (
                              <option key={cl.id} value={cl.id} disabled={!cl.primaryUserId}>
                                {cl.name}
                                {!cl.primaryUserId ? " (нет user_id)" : ""}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          <button
                            type="button"
                            disabled={
                              !linkClientByChat[c.chatId] || linkChatMutation.isPending
                            }
                            onClick={() =>
                              linkChatMutation.mutate({
                                chatId: c.chatId,
                                clientId: linkClientByChat[c.chatId],
                                title: c.chatTitle,
                              })
                            }
                            className="px-2 py-1 rounded border border-input hover:bg-muted disabled:opacity-50"
                          >
                            Привязать
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mt-2">
                Непривязанных групп за 90 дней не найдено.
              </p>
            )}
          </details>
          {backfillPreview && (
            <div className="text-xs border-t border-border pt-2">
              Превью: {backfillPreview.previews.length} тикетов (показано до 30). Примеры:{" "}
              {backfillPreview.previews
                .slice(0, 3)
                .map((p) => p.subject)
                .join("; ")}
            </div>
          )}
        </div>
      )}

      {autoDiag && showMaxSetup && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-3">
          <h2 className="text-base font-semibold">Max → тикеты</h2>
          <p className="text-sm text-muted-foreground">
            Каналы Max привязываются в реестре клиентов так же, как Telegram. Ниже — сколько тикетов
            можно догнать из архива Max.
          </p>
          {autoDiag.schemaReady && (
            <span
              className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                autoDiag.autoEnabled
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-amber-100 text-amber-800"
              }`}
            >
              {autoDiag.autoEnabled ? "Автосоздание включено" : "Автосоздание выключено"}
            </span>
          )}
          <div className="flex flex-wrap gap-6 text-sm">
            <div>
              <span className="text-muted-foreground">Можно догнать за 7 дней</span>
              <div className="font-medium">{autoDiag.pendingMaxBackfill}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Сообщений за 7 дней</span>
              <div className="font-medium">{autoDiag.maxMessagesLast7d}</div>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => previewBackfillMutation.mutate()}
              disabled={previewBackfillMutation.isPending || !autoDiag.schemaReady}
              className="px-3 py-1.5 text-sm rounded-md border border-input hover:bg-muted disabled:opacity-50"
            >
              {previewBackfillMutation.isPending ? "Считаю…" : "Превью"}
            </button>
            <button
              type="button"
              onClick={() => applyBackfillMutation.mutate()}
              disabled={
                applyBackfillMutation.isPending ||
                !autoDiag.schemaReady ||
                autoDiag.pendingMaxBackfill === 0
              }
              className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {applyBackfillMutation.isPending ? "Создаю…" : "Создать тикеты за 7 дней"}
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {statsData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="rounded-lg border border-amber-300 bg-amber-50/80 dark:bg-amber-950/40 p-4">
            <div className="text-sm text-amber-900 dark:text-amber-200">Нужен ваш ответ</div>
            <div className="text-2xl font-bold text-amber-950 dark:text-amber-100">
              {statsData.needsReplyCount ?? 0}
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">Жду клиента</div>
            <div className="text-2xl font-bold">{statsData.waitingClientCount ?? 0}</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">Открытых</div>
            <div className="text-2xl font-bold">{statsData.openCount}</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">В работе</div>
            <div className="text-2xl font-bold">{statsData.inProgressCount}</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">Ожидают ответа</div>
            <div className="text-2xl font-bold">{statsData.waitingUserCount}</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">Новых сегодня</div>
            <div className="text-2xl font-bold">{statsData.newToday}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Поиск по теме, email..."
            className="pl-9 pr-4 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring w-64"
          />
        </div>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 rounded-md border border-input bg-background text-sm"
        >
          <option value="">Все статусы</option>
          <option value="open">Открытые</option>
          <option value="in_progress">В работе</option>
          <option value="waiting_user">Ожидают ответа</option>
          <option value="resolved">Решённые</option>
          <option value="closed">Закрытые</option>
        </select>
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 rounded-md border border-input bg-background text-sm"
        >
          <option value="">Все категории</option>
          <option value="general">Общий</option>
          <option value="billing">Биллинг</option>
          <option value="technical">Техническая</option>
          <option value="feature_request">Предложение</option>
        </select>
        <select
          value={priority}
          onChange={(e) => {
            setPriority(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 rounded-md border border-input bg-background text-sm"
        >
          <option value="">Все приоритеты</option>
          <option value="low">Низкий</option>
          <option value="normal">Обычный</option>
          <option value="high">Высокий</option>
          <option value="urgent">Срочный</option>
        </select>
        <select
          value={assignedTo}
          onChange={(e) => {
            setAssignedTo(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 rounded-md border border-input bg-background text-sm"
        >
          <option value="">Все ответственные</option>
          <option value="me">Мои тикеты</option>
          <option value="unassigned">Без ответственного</option>
          {adminsList?.map((a) => (
            <option key={a.id} value={a.id}>
              {a.email}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  {workQueue === "needs_reply" ? "Обращение" : "Тема"}
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  {source === "telegram" ? "Отправитель" : "Пользователь"}
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Категория
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Приоритет
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Статус
                </th>
                {!source && (
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                    Источник
                  </th>
                )}
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Ответственный
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Сообщ.
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Создан
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Посл. ответ
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: source ? 9 : 10 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data?.tickets.length === 0 ? (
                <tr>
                  <td
                    colSpan={source ? 9 : 10}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    {workQueue === "needs_reply"
                      ? "Нет тикетов, где ждут вашего ответа. Если в Telegram есть новые сообщения — нажмите «Создать тикеты за 7 дней» во вкладке Telegram."
                      : "Тикеты не найдены"}
                  </td>
                </tr>
              ) : groupedBySender ? (
                groupedBySender.map((group) => (
                  <Fragment key={group.label}>
                    <tr className="bg-muted/40">
                      <td
                        colSpan={9}
                        className="px-4 py-2 text-sm font-semibold text-foreground"
                      >
                        {group.label}
                        <span className="ml-2 font-normal text-muted-foreground">
                          {group.tickets.length}{" "}
                          {group.tickets.length === 1 ? "тикет" : "тикета"}
                          {group.tickets.reduce((n, t) => n + t.message_count, 0) > 1
                            ? ` · ${group.tickets.reduce((n, t) => n + t.message_count, 0)} сообщ.`
                            : ""}
                        </span>
                      </td>
                    </tr>
                    {group.tickets.map((t) => renderTicketRow(t))}
                  </Fragment>
                ))
              ) : groupedTickets ? (
                groupedTickets.map((group) => (
                  <Fragment key={group.source}>
                    <tr className="bg-muted/40">
                      <td
                        colSpan={10}
                        className="px-4 py-2 text-sm font-semibold text-foreground"
                      >
                        {getSourceLabel(group.source)}
                        <span className="ml-2 font-normal text-muted-foreground">
                          {group.tickets.length} на странице
                        </span>
                      </td>
                    </tr>
                    {group.tickets.map((t) => renderTicketRow(t))}
                  </Fragment>
                ))
              ) : (
                data?.tickets.map((t) => renderTicketRow(t))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Стр. {data.page} из {data.totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-md border border-input text-sm disabled:opacity-50 hover:bg-muted"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() =>
                setPage((p) => Math.min(data.totalPages, p + 1))
              }
              disabled={page >= data.totalPages}
              className="px-3 py-1.5 rounded-md border border-input text-sm disabled:opacity-50 hover:bg-muted"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
